import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ProfilePage from "./screens/ProfilePage/ProfilePage";
import AdminPage from "./screens/AdminPage/AdminPage";
import DriverPage from "./screens/DriverPage/DriverPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { RealtimeProvider } from "./context/RealtimeContext";
import Header from "./components/Header/Header";
import { ConsentProvider } from "./consent/ConsentProvider";
import AnalyticsLoader from "./analytics/AnalyticsLoader";
import AppFooter from "./components/AppFooter/AppFooter";
import HomePage from "./screens/HomePage/HomePage";
import KontaktPage from "./screens/KontaktPage/KontaktPage";
import DailyMenuPage from "./screens/DailyMenuPage/DailyMenuPage";
import SiteGate from "./components/SiteGate/SiteGate";
import SetupPassword from "./screens/SetupPassword/SetupPassword";
import VOPPage from "./screens/VOPPage/VOPPage";
import GDPRPage from "./screens/GDPRPage/GDPRPage";
import SupportWidget from "./components/SupportWidget/SupportWidget";
import { TenantProvider, useTenant } from "./context/TenantContext";
import LandingPage from "./screens/LandingPage/LandingPage";
import PlatformPage from "./screens/PlatformPage/PlatformPage";
import ObjednavkaPage from "./screens/ObjednavkaPage/ObjednavkaPage";
import RegistraciaPage from "./screens/RegistraciaPage/RegistraciaPage";
import MojeObjednavkyPage from "./screens/MojeObjednavkyPage/MojeObjednavkyPage";
import PozvankaPage from "./screens/PozvankaPage/PozvankaPage";
import ActingAsBanner from "./components/ActingAsBanner/ActingAsBanner";
import SuhlasPodmienky from "./components/SuhlasPodmienky/SuhlasPodmienky";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import { VzhladProvider } from "./context/VzhladContext";

function ProtectedRoute({ children }) {
  const { isLoggedIn, authLoading } = useAuth();
  if (authLoading) return null;
  if (!isLoggedIn) return <Navigate to="/" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { isLoggedIn, isAdmin, authLoading, userRole } = useAuth();
  if (authLoading) return null;
  const canAccess = isLoggedIn && (isAdmin || userRole === 'MANAGER');
  if (!canAccess) return <Navigate to="/" replace />;
  return children;
}

// ADMIN a SUPERADMIN (act-as z /sprava), MANAGER nie. SUPERADMIN tu chybal,
// hoci backend (/api/customers je za authAdminOrSuperAdmin) ho pusta --
// spravca v prevadzke tak nevidel Zakaznikov, ktorych ma pomoct nastavit.
function AdminOnlyRoute({ children }) {
  const { isLoggedIn, authLoading, userRole } = useAuth();
  if (authLoading) return null;
  if (!isLoggedIn || (userRole !== 'ADMIN' && userRole !== 'SUPERADMIN')) return <Navigate to="/" replace />;
  return children;
}

function AdminPricingRoute({ children }) {
  const { isLoggedIn, authLoading, userRole } = useAuth();
  if (authLoading) return null;
  const canAccess = isLoggedIn && (userRole === 'ADMIN' || userRole === 'SUPERADMIN');
  if (!canAccess) return <Navigate to="/admin" replace />;
  return children;
}

function DriverRoute({ children }) {
  const { isLoggedIn, authLoading, userRole } = useAuth();
  if (authLoading) return null;
  if (!isLoggedIn || userRole !== 'DRIVER') return <Navigate to="/" replace />;
  return children;
}

/**
 * Obrazovky, ktore patria VYLUCNE zakaznikovi (moje objednavky, profil
 * objednavok). Obsluha na ne nepatri -- pracuju s vlastnymi datami
 * prihlaseneho a dispecer ma na objednavky svoje obrazovky.
 *
 * Toto je len pohodlie: skutocnou hranicou je server (`authCustomer`).
 * Rovnako plati opacny smer -- CUSTOMER sa cez AdminRoute, AdminOnlyRoute,
 * AdminPricingRoute ani DriverRoute neprepise, lebo v ich zoznamoch roli nie je
 * (R51), a backend ho odmietne aj tak.
 */
function CustomerRoute({ children }) {
  const { isLoggedIn, authLoading, userRole } = useAuth();
  if (authLoading) return null;
  if (!isLoggedIn || userRole !== 'CUSTOMER') return <Navigate to="/" replace />;
  return children;
}

/**
 * Obrazovky PREVADZKY (rozvozy.sk/<slug>/... alebo vlastna domena).
 * Cesty su bez prefixu -- basename (index.js) ho doplni sam.
 */
function TenantApp() {
  const location = useLocation();
  const { branding } = useTenant();
  const { userRole } = useAuth();

  // Obsluha pracuje v paneli s vlastnou bocnou listou, takze paticka s odkazmi
  // pre navstevnikov tam len zavadza. Zakaznik ziadny panel nema -- `/profil`
  // je pre neho obycajna stranka webu, takze paticku dostane rovnako ako
  // na ostatnych strankach.
  const panelovaCesta =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/customers") ||
    location.pathname.startsWith("/vodic") ||
    location.pathname.startsWith("/setup-password") ||
    (location.pathname.startsWith("/profil") && userRole !== "CUSTOMER");
  const hideFooter = panelovaCesta;
  return (
          <>
          {/* Musi byt VNUTRI AuthProvider -- spojenie sa otvara az po prihlaseni. */}
          <RealtimeProvider>
          <div className="App">
            <ActingAsBanner />
            {/* Nové znenie podmienok si vypýta súhlas (R6). Komponent sa
                postará o to, komu sa okno ukáže — tu je len raz, aby ho
                zákazník videl bez ohľadu na to, kde práve je. */}
            <SuhlasPodmienky />
            {/* Header */}
            <Header logo={branding.logoUrl} />

            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="kontakt" element={<KontaktPage />} />
              <Route path="denne-menu" element={<DailyMenuPage />} />
              <Route path="vop" element={<VOPPage />} />
              <Route path="ochrana-osobnych-udajov" element={<GDPRPage />} />
              <Route path="setup-password" element={<SetupPassword />} />
              <Route
                path="profil"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                }
              />
              <Route
                path="admin/cenniky"
                element={
                  <AdminPricingRoute>
                    <AdminPage initialSection="pricing-lists" />
                  </AdminPricingRoute>
                }
              />
              <Route
                path="admin/cenniky/zoznam"
                element={
                  <AdminPricingRoute>
                    <AdminPage initialSection="pricing-lists" />
                  </AdminPricingRoute>
                }
              />
              <Route
                path="admin/cenniky/ceny"
                element={
                  <AdminPricingRoute>
                    <AdminPage initialSection="pricing-rates" />
                  </AdminPricingRoute>
                }
              />
              <Route
                path="customers"
                element={
                  <AdminOnlyRoute>
                    <AdminPage initialSection="customers" />
                  </AdminOnlyRoute>
                }
              />
              <Route
                path="vodic"
                element={
                  <DriverRoute>
                    <DriverPage />
                  </DriverRoute>
                }
              />
              {/* Rezervovane pre zakaznicke objednavanie -- aby sa cesta nemusela neskor menit */}
              {/* Menu a kosik sa daju prezerat aj bez prihlasenia (R41) --
                  prihlasenie si vypyta az odoslanie objednavky. */}
              <Route path="objednavka" element={<ObjednavkaPage />} />
              <Route path="registracia" element={<RegistraciaPage />} />
              <Route path="pozvanka" element={<PozvankaPage />} />
              <Route
                path="moje-objednavky"
                element={
                  <CustomerRoute>
                    <MojeObjednavkyPage />
                  </CustomerRoute>
                }
              />
              <Route path="*" element={<h1>Stránka nenájdená</h1>} />
            </Routes>
            {/* Mimo <Routes>, takže sa pri navigácii neodmountuje a rozpísané
                hlásenie prežije zmenu obrazovky. Sám si číta auth stav. */}
            <SupportWidget />
            {!hideFooter && <AppFooter />}
          </div>
          <AnalyticsLoader />
          </RealtimeProvider>
          </>
  );
}

/** Podla rezimu z URL: marketing, platformova sprava, alebo prevadzka. */
function AppPodlaRezimu() {
  const { mode } = useTenant();
  if (mode === "root") return <LandingPage />;
  if (mode === "platform") return <PlatformPage />;
  return <TenantApp />;
}

// Poradie providerov: VzhladProvider -> SiteGate -> TenantProvider -> ConsentProvider -> AuthProvider.
// VzhladProvider uplne navonok: svetly/tmavy rezim plati aj pre obrazovku s heslom.
// TenantProvider pred AuthProvider: overenie prihlasenia porovnava firmu tokenu
// s prevadzkou z URL, takze ju musi poznat skor.
function App() {
  return (
    <VzhladProvider>
      <SiteGate>
        {/* Pre všetky režimy (prevádzka, marketing, platforma) naraz. */}
        <ScrollToTop />
        <TenantProvider>
          <ConsentProvider>
            <AuthProvider>
              <AppPodlaRezimu />
            </AuthProvider>
          </ConsentProvider>
        </TenantProvider>
      </SiteGate>
    </VzhladProvider>
  );
}

export default App;
